import React, { useState } from "react";
import {
  MoreVertical,
  Phone,
  MessageCircle,
  Mail,
  MapPin,
  Eye,
  Edit,
  Trash2,
  Users,
  UserPlus,
  Loader2,
  ShieldCheck,
} from "lucide-react";

const NetworkPartnersTab = ({
  partners = [],
  isLoading = false,
  searchTerm = "",
  onOpenAddModal,
  onViewDetails,
  onEditPartner,
  onDeletePartner,
}) => {
  const [activeMenuId, setActiveMenuId] = useState(null);

  const toggleMenu = (id, e) => {
    e.stopPropagation();
    setActiveMenuId((prev) => (prev === id ? null : id));
  };

  // Close context menu on outside click
  React.useEffect(() => {
    const handleOutside = () => setActiveMenuId(null);
    window.addEventListener("click", handleOutside);
    return () => window.removeEventListener("click", handleOutside);
  }, []);

  if (isLoading) {
    return (
      <div className="net-loading-wrap">
        <Loader2 size={36} className="net-spin" style={{ color: "#2563eb" }} />
        <p style={{ fontWeight: 600 }}>Loading network partners...</p>
      </div>
    );
  }

  if (partners.length === 0) {
    return (
      <div className="net-empty">
        <div className="net-empty__icon-wrap">
          <Users size={32} />
        </div>
        <h3 className="net-empty__title">
          {searchTerm ? "No matching partners found" : "No Network Partners Yet"}
        </h3>
        <p className="net-empty__desc">
          {searchTerm
            ? `No partners matched your search "${searchTerm}". Try a different keyword or filter.`
            : "Expand your real estate network! Connect with brokers, agents, and associate partners to collaborate on deals and earn reward points."}
        </p>
        <button
          type="button"
          className="net-btn net-btn--primary"
          onClick={onOpenAddModal}
        >
          <UserPlus size={16} /> Add First Partner
        </button>
      </div>
    );
  }

  return (
    <div className="net-grid">
      {partners.map((p) => {
        const id = p.id || p.network_id || p.userId;
        const name = `${p.first_name || p.firstName || ""} ${p.last_name || p.lastName || ""}`.trim() || "Network Partner";
        const mobile = p.mobile_number || p.mobileNo || "";
        const email = p.email_address || p.emailId || "";
        const city = p.city_name || p.city || "";
        const state = p.state_name || p.state || "";
        const area = p.area_name || p.area || "";
        const cleanPhone = mobile.replace(/^91/, "");
        const whatsappUrl = cleanPhone ? `https://wa.me/91${cleanPhone}?text=Hi%20${encodeURIComponent(name)}` : null;

        return (
          <div key={id} className="net-card">
            <div>
              {/* Card Header */}
              <div className="net-card__header">
                <div className="net-card__profile" onClick={() => onViewDetails(p)} style={{ cursor: "pointer" }}>
                  <div className="net-card__avatar">
                    {name.charAt(0).toUpperCase()}
                  </div>
                  <div className="net-card__name-col">
                    <h4 className="net-card__name" title={name}>
                      {name}
                    </h4>
                    <span className="net-card__type-tag">Associate Partner</span>
                  </div>
                </div>

                {/* Context Menu Dropdown */}
                <div className="net-menu-wrapper">
                  <button
                    type="button"
                    className="net-menu-trigger"
                    onClick={(e) => toggleMenu(id, e)}
                    title="Actions"
                  >
                    <MoreVertical size={16} />
                  </button>

                  {activeMenuId === id && (
                    <div className="net-dropdown-menu" onClick={(e) => e.stopPropagation()}>
                      <button
                        type="button"
                        className="net-dropdown-item"
                        onClick={() => {
                          setActiveMenuId(null);
                          onViewDetails(p);
                        }}
                      >
                        <Eye size={14} /> View Details
                      </button>
                      <button
                        type="button"
                        className="net-dropdown-item"
                        onClick={() => {
                          setActiveMenuId(null);
                          onEditPartner(p);
                        }}
                      >
                        <Edit size={14} /> Edit Partner
                      </button>
                      <div className="net-dropdown-divider" />
                      <button
                        type="button"
                        className="net-dropdown-item net-dropdown-item--danger"
                        onClick={() => {
                          setActiveMenuId(null);
                          onDeletePartner(p);
                        }}
                      >
                        <Trash2 size={14} /> Remove Partner
                      </button>
                    </div>
                  )}
                </div>
              </div>

              {/* Card Details */}
              <div className="net-card__details">
                {mobile && (
                  <div className="net-card__detail-row">
                    <Phone size={13} className="net-card__detail-icon" />
                    <span>+91 {cleanPhone}</span>
                  </div>
                )}
                {email && (
                  <div className="net-card__detail-row">
                    <Mail size={13} className="net-card__detail-icon" />
                    <span>{email}</span>
                  </div>
                )}
                {(city || state || area) && (
                  <div className="net-card__detail-row">
                    <MapPin size={13} className="net-card__detail-icon" />
                    <span>{[area, city, state].filter(Boolean).join(", ")}</span>
                  </div>
                )}
              </div>
            </div>

            {/* Quick Action Footer */}
            <div className="net-card__actions-row">
              {mobile && (
                <a href={`tel:${mobile}`} className="net-quick-btn" title="Call Partner">
                  <Phone size={13} /> Call
                </a>
              )}
              {whatsappUrl && (
                <a
                  href={whatsappUrl}
                  target="_blank"
                  rel="noopener noreferrer"
                  className="net-quick-btn net-quick-btn--wa"
                  title="WhatsApp"
                >
                  <MessageCircle size={13} /> WhatsApp
                </a>
              )}
              <button
                type="button"
                className="net-quick-btn"
                onClick={() => onViewDetails(p)}
                title="View Details"
              >
                <Eye size={13} /> Profile
              </button>
            </div>
          </div>
        );
      })}
    </div>
  );
};

export default NetworkPartnersTab;
